import type { JSX, ReactNode, RefObject } from 'react';
import { Fragment, useEffect, useRef } from 'react';
import type { WireChatMessage } from '@logistics/contracts';
import { AgentAnswer, AgentError, AgentFrame, Thinking } from './agent-message';
import { stepText } from './chat-format';
import { EmptyThread } from './empty-thread';
import { UserMessage } from './user-message';
import { livePieces, unansweredQuestions } from './live-turn';
import type { LiveTurn } from './live-turn';

export interface ThreadHeader {
  readonly title: string;
  readonly meta: string;
  readonly scope: string;
  /** ☰, where the list is not a column: a phone's overlay, or a tablet with an artifact open. */
  readonly onShowList?: (() => void) | undefined;
}

/**
 * The middle column: the header, the messages, then the composer. Messages
 * are keyed by position — the thread only ever grows — so a live turn becomes
 * its stored messages in place, without remounting the answer being typed.
 */
export function Thread({
  header,
  messages,
  turn,
  typing,
  showingId,
  onTyped,
  onOpenArtifact,
  onAsk,
  onRetry,
  onSchedule,
  askDisabled,
  status,
  composer,
}: {
  header: ThreadHeader;
  messages: readonly WireChatMessage[];
  turn: LiveTurn | null;
  typing: ReadonlySet<string>;
  showingId: string | null;
  onTyped: (message: WireChatMessage) => void;
  onOpenArtifact: (id: string) => void;
  onAsk: (text: string) => void;
  /** Asks a question again; absent where the person may not ask (someone else's). */
  onRetry?: ((text: string) => void) | undefined;
  /** Schedules a question to run again; absent for anyone but an admin. */
  onSchedule?: ((text: string) => void) | undefined;
  askDisabled: boolean;
  status?: ReactNode;
  composer: ReactNode;
}): JSX.Element {
  const live = livePieces(turn, messages);
  const empty = messages.length === 0 && turn === null && status === undefined;
  const orphans = unansweredQuestions(messages, turn);
  const scroller = useStickToBottom(turn?.key);
  const retry = (text: string): (() => void) | undefined => bind(onRetry, text);

  return (
    <div className="relative flex h-full min-h-0 min-w-0 flex-col">
      <ThreadHead header={header} />
      <div ref={scroller.outer} className="min-h-0 flex-1 overflow-y-auto px-6 pt-6 pb-2">
        <div ref={scroller.inner} className="flex min-h-full flex-col gap-[22px]">
          {status}
          {empty && <EmptyThread onAsk={onAsk} disabled={askDisabled} />}
          {messages.map((m, i) =>
            m.role === 'user' ? (
              <Fragment key={i}>
                <UserMessage message={m} onSchedule={bind(onSchedule, m.text)} />
                {orphans.has(i) && (
                  <AgentError text="the answer was not finished" onRetry={retry(m.text)} />
                )}
              </Fragment>
            ) : (
              <AgentAnswer
                key={i}
                message={m}
                animate={typing.has(m.id)}
                showing={showingId === m.id}
                onTyped={() => onTyped(m)}
                onOpenArtifact={() => onOpenArtifact(m.id)}
              />
            ),
          )}
          {turn !== null && live.user && <UserMessage key={messages.length} message={turn.user} />}
          {turn !== null && live.thinking && (
            <AgentFrame key={messages.length + 1}>
              <Thinking text={stepText(turn.step)} />
            </AgentFrame>
          )}
          {turn !== null && live.error !== undefined && (
            <AgentError
              key={messages.length + 1}
              text={live.error}
              onRetry={retry(turn.user.text)}
            />
          )}
        </div>
      </div>
      {composer}
    </div>
  );
}

/** A handler for one message's text, or nothing when the action is not offered. */
function bind(
  action: ((text: string) => void) | undefined,
  text: string,
): (() => void) | undefined {
  return action === undefined ? undefined : () => action(text);
}

function ThreadHead({ header }: { header: ThreadHeader }): JSX.Element {
  return (
    <div className="border-line flex flex-none items-center justify-between gap-3 border-b px-6 py-3.5">
      {header.onShowList !== undefined && (
        <button
          type="button"
          onClick={header.onShowList}
          title="Show conversations"
          aria-label="Show conversations"
          className="border-line text-mute size-[30px] flex-none rounded-lg border bg-transparent text-sm"
        >
          ☰
        </button>
      )}
      <div className="min-w-0 flex-1">
        <div className="truncate text-[14.5px] font-semibold">{header.title}</div>
        <div className="text-mute font-mono text-[10.5px] tracking-[.06em]">{header.meta}</div>
      </div>
      <div className="text-mute flex items-center gap-1.5 font-mono text-[11px] whitespace-nowrap">
        <span className="bg-ok size-1.5 rounded-full" />
        {header.scope}
      </div>
    </div>
  );
}

/**
 * Keeps the thread pinned to the newest line while it grows — a conversation
 * loading, a step, each word typed — unless the reader has scrolled up to read.
 */
function useStickToBottom(turnKey: number | undefined): {
  outer: RefObject<HTMLDivElement | null>;
  inner: RefObject<HTMLDivElement | null>;
} {
  const outer = useRef<HTMLDivElement | null>(null);
  const inner = useRef<HTMLDivElement | null>(null);
  const pinned = useRef(true);

  useEffect(() => {
    const box = outer.current;
    const content = inner.current;
    if (box === null || content === null) return undefined;
    const onScroll = (): void => {
      pinned.current = box.scrollHeight - box.scrollTop - box.clientHeight < 80;
    };
    const observer = new ResizeObserver(() => {
      if (pinned.current) box.scrollTop = box.scrollHeight;
    });
    box.addEventListener('scroll', onScroll, { passive: true });
    observer.observe(content);
    box.scrollTop = box.scrollHeight;
    return () => {
      box.removeEventListener('scroll', onScroll);
      observer.disconnect();
    };
  }, []);

  // A question just sent: follow it down even if the reader had scrolled up.
  useEffect(() => {
    const box = outer.current;
    if (turnKey === undefined || box === null) return;
    pinned.current = true;
    box.scrollTop = box.scrollHeight;
  }, [turnKey]);

  return { outer, inner };
}
